import {
	EuiButton,
	EuiButtonEmpty, EuiConfirmModal, EuiFieldNumber, EuiFieldText,
	EuiFlexGroup,
	EuiFlexItem,
	EuiFlyout,
	EuiFlyoutBody,
	EuiFlyoutFooter,
	EuiFlyoutHeader, EuiFormRow, EuiPanel, EuiSpacer, EuiText,
	EuiTitle
} from '@equipmentshare/ds2'
import useConvertPartManyToManyHandler from '../../effects/convert-part-many-to-many'
import React, { useEffect, useState } from 'react'
import { Part, useStorePartDetailsLazyQuery } from '../../../../graphql'
import { heapEvent } from '@equipmentshare/heaper'
import PartPicker from '../../../../components/PartPicker'
import { formatCurrency } from '../../../helpers/utils'
import { StorePart } from '../../../../types/storePart'

export type ConvertPartFlyoutProps = {
  storePart: StorePart,
  onClose: () => void,
  onSuccess: () => void,
}
export default function ConvertPartFlyout( { storePart, onClose, onSuccess }: ConvertPartFlyoutProps ) {
	const convertPartManyToMany = useConvertPartManyToManyHandler()
	const [ loadStorePartDetails, { loading: loadingStorePartDetails } ] = useStorePartDetailsLazyQuery( { errorPolicy: 'ignore' } )

	const [ part, setPart ] = useState<Part>()
	const [ selectedStorePart, setSelectedStorePart ] = useState<StorePart>()
	const [ quantityFrom, setQuantityFrom ] = useState<number>( 1 )
	const [ eachConversionQuantity, setEachConversionQuantity ] = useState<number>( 1 )
	const [ quantityTo, setQuantityTo ] = useState<number>( 1 )
	const [ bin, setBin ] = useState<string>( '' )
	const [ isSaving, setIsSaving ] = useState( false )
	const [ isConfirmModalVisible, setIsConfirmModalVisible ] = useState( false )

	const handlePartChange = ( _id: number, selectedPart?: Part ) => setPart( selectedPart )
	const handleQuantityFromChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
		setQuantityFrom( parseInt( event.target.value.replace( /\D/g, '' ), 10 ) )
	}
	const handleEachConversionQuantity = ( event: React.ChangeEvent<HTMLInputElement> ) => {
		setEachConversionQuantity( parseInt( event.target.value.replace( /\D/g, '' ), 10 ) )
	}
	useEffect( () => {
		const newQuantityTo = quantityFrom && eachConversionQuantity && quantityFrom * eachConversionQuantity
		setQuantityTo( newQuantityTo )
	}, [ quantityFrom, eachConversionQuantity ] )

	useEffect( () => {
		( async() => {
			if ( part ) {
				const { data } = await loadStorePartDetails( { variables: { partId: part.id, storeId: parseInt( storePart.inventoryLocation.id, 10 ) } } )
				if ( data?.storePart ) {
					setSelectedStorePart( data.storePart )
				}
			}
		} )()
	}, [ part ] )

	const handleBinChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
		setBin( event.target.value )
	}

	const handleSubmit = async() => {
		heapEvent( 'Inventory - Convert Items Modal - Click - Submit', {} )
		setIsSaving( true )
		return await convertPartManyToMany( storePart, part?.id?.toString() ?? '', quantityFrom, quantityTo )
			.then( onSuccess )
	}

	const handleCancel = () => {
		heapEvent( 'Inventory - Convert Items Modal - Click - Cancel', {} )
		onClose()
	}

	const handleClose = () => {
		heapEvent( 'Inventory - Convert Items Modal - Click - X button', {} )
		onClose()
	}

	const onConfirmModalCancel = () => setIsConfirmModalVisible( false )

	const onConfirmModalConfirmation = async() => {
		onConfirmModalCancel()
		await handleSubmit()
		onClose()
	}

	const getLongPartName = ( partToName: Part | StorePart['part'] ) => `${partToName.partNumber} - ${partToName.partClass.description}`

	const isValid = !!quantityFrom && !!quantityTo && !!part && quantityFrom <= storePart.availableQuantity
	return (
		<>
			<EuiFlyout
				ownFocus
				onClose={handleClose}
				maxWidth={420}
			>
				<EuiFlyoutHeader hasBorder>
					<EuiTitle size="m">
						<h2>Convert Item(s)</h2>
					</EuiTitle>
				</EuiFlyoutHeader>
				<EuiFlyoutBody>
					<EuiFlexGroup direction="column" gutterSize="m">
						<EuiFlexItem grow={false}>
							<EuiPanel hasShadow={false} hasBorder>
								<EuiFormRow label="From">
									<EuiText size="s">{getLongPartName( storePart.part )}</EuiText>
								</EuiFormRow>
								<EuiSpacer size="s" />
								<EuiFormRow>
									<PartPicker
										id="part"
										label={<>To {!part && <span className="required">*</span>}</>}
										onChange={handlePartChange}
										selectedPart={part}
									/>
								</EuiFormRow>
							</EuiPanel>
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<EuiPanel hasShadow={false} hasBorder>
								<EuiFlexGroup gutterSize="m">
						      <EuiFlexItem>
										<EuiFormRow label={<>Quantity to convert {!quantityFrom && <span className="required">*</span>}</>}>
											<EuiFieldNumber
												id="quantity-to-convert"
												onChange={handleQuantityFromChange}
												value={quantityFrom}
												min={1}
												max={storePart.availableQuantity}
												placeholder="Enter a quantity"
												step={1}
												isInvalid={quantityFrom > storePart.availableQuantity}
											/>
										</EuiFormRow>
										<EuiText size="xs" color={quantityFrom > storePart.availableQuantity ? 'danger' : 'inherit'}>
                      Available: {storePart.availableQuantity}
										</EuiText>
									</EuiFlexItem>
									<EuiFlexItem>
										<EuiFormRow label={<>Multiply by {!eachConversionQuantity && <span className="required">*</span>}</>}>
											<EuiFieldNumber
												id="each-conversion-quantity"
												onChange={handleEachConversionQuantity}
												value={eachConversionQuantity}
												min={1}
												placeholder="Enter a quantity"
												step={1}
											/>
										</EuiFormRow>
									</EuiFlexItem>
								</EuiFlexGroup>
								{
									storePart.weightedAverageCost && isValid &&
                  <>
                  	<EuiSpacer size="m" />
                  	<EuiText size="xs">
                      Total new quantity for {part.partClass.description}:
                  	</EuiText>
                  	<EuiText size="m">
                  		<b>
                  			{quantityFrom} &times;&nbsp;
                  			{eachConversionQuantity} =&nbsp;
                  			{quantityTo}
                  		</b>
                  	</EuiText>
                  	<EuiSpacer size="s" />
                  	<EuiText size="xs">
                      New cost for each {part.partClass.description}:
                  	</EuiText>
                  	<EuiText size="m">
                  		<b>
                  			{formatCurrency( ( storePart.weightedAverageCost.amount / eachConversionQuantity ).toString() )}
                  		</b>
                  	</EuiText>
                  </>
								}
							</EuiPanel>
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<EuiFormRow label="Bin (optional)">
								<EuiFieldText
									id="bin"
									onChange={handleBinChange}
									value={bin}
								/>
							</EuiFormRow>
						</EuiFlexItem>
					</EuiFlexGroup>

				</EuiFlyoutBody>
				<EuiFlyoutFooter>
					<EuiFlexGroup justifyContent="spaceBetween">
						<EuiFlexItem grow={false}>
							<EuiButtonEmpty
								onClick={handleCancel}
								flush="left">
                Cancel
							</EuiButtonEmpty>
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<EuiButton
								fill
								onClick={() => setIsConfirmModalVisible( true )}
								isLoading={isSaving}
								isDisabled={!isValid}>
                Convert
							</EuiButton>
						</EuiFlexItem>
					</EuiFlexGroup>
				</EuiFlyoutFooter>
			</EuiFlyout>
			{
				isConfirmModalVisible && !loadingStorePartDetails &&
        <EuiConfirmModal
        	style={{ width: 600 }}
        	title="Are you sure?"
        	onCancel={onConfirmModalCancel}
        	onConfirm={onConfirmModalConfirmation}
        	cancelButtonText="Cancel"
        	confirmButtonText="Yes"
        	buttonColor="warning"
        >
        	<EuiText>
            Do you want to convert <b>{quantityFrom}</b> {getLongPartName( storePart.part )} into <b>{quantityTo}</b> {part && getLongPartName( part )}? Any changes you make can&apos;t be undone.
        	</EuiText>
        	<EuiSpacer />
        	<EuiText>
            Inventory updates:<br />
        		{storePart.part.partClass.description} reduced from <b>{storePart.availableQuantity}</b> to <b>{storePart.availableQuantity - quantityFrom}</b><br />
        		{part?.partClass.description} expanded from <b>{selectedStorePart?.availableQuantity ?? 0}</b> to <b>{( selectedStorePart?.availableQuantity ?? 0 ) + quantityTo}</b>
        	</EuiText>
        </EuiConfirmModal>
			}
		</>
	)
}
